
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { hasLaunchDatePassed } from '@/utils/date-constants';

type LogoProps = {
  className?: string;
  onClick?: () => void;
  variant?: 'light' | 'dark';
};

const Logo = ({ className = '', onClick, variant }: LogoProps) => {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const isLaunched = hasLaunchDatePassed();
  
  // Set up an effect to check for theme changes
  useEffect(() => {
    // Initial theme check
    const checkTheme = () => {
      const isDark = document.documentElement.classList.contains('dark');
      setIsDarkMode(isDark);
    };
    
    // Check on mount
    checkTheme();
    
    // Set up a MutationObserver to watch for class changes on html element
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          checkTheme();
        }
      });
    });
    
    // Start observing the html element for class changes
    observer.observe(document.documentElement, { attributes: true });
    
    // Clean up observer on unmount
    return () => observer.disconnect();
  }, []);

  // Determine the destination URL based on launch status
  const homeUrl = isLaunched ? "/" : "/cayman";
  const homeNavState = isLaunched ? { noRedirect: true } : {};
  
  // Determine which logo to use based on variant prop or dark mode detection
  const logoSrc = variant === 'dark' 
    ? '/img/nationcard-dark.png' 
    : variant === 'light'
      ? '/img/nationcard-light.png'
      : isDarkMode
        ? '/img/nationcard-dark.png'
        : '/img/nationcard-light.png';

  // If onClick is provided, use a regular div with onClick instead of Link
  if (onClick) {
    return (
      <div 
        className={`flex items-center gap-2 ${className} ${onClick ? 'cursor-pointer' : ''}`} 
        onClick={onClick}
      >
        <img 
          src={logoSrc}
          alt="NationCard™ Logo" 
          className="h-8 w-auto object-contain" 
        />
      </div>
    );
  }

  // Default behavior with Link - conditionally routes to / or /cayman
  return (
    <Link to={homeUrl} state={homeNavState} className={`flex items-center gap-2 ${className}`}>
      <img 
        src={logoSrc}
        alt="NationCard™ Logo" 
        className="h-8 w-auto object-contain"
      />
    </Link>
  );
};

export default Logo;
